import React from 'react';
import { Grid, LinearProgress } from '@mui/material';
import DocNotFound from '../tool/DocNotFound';
import ReviewDoc from './doc/ReviewDoc';
import ReviewForm from './form/ReviewForm';
import ReviewNav from './ReviewNav';
import useGlynt from '../hooks/useGlynt';
import { AppContext } from '../../App';
import { IDocument, IExtraction, IExtractionBatch } from '../../types/my-types';
import { MarkContextProvider } from '../../context/MarkContext';
import { getIdFromUrl } from '../../utils/getIdFromUrl';
import { useQueryClient } from '@tanstack/react-query';
import { IResultsType } from '../../types/spreadsheet-types';

export interface ReviewGroupProps {
  ebId: string;
  extractionBatch: IExtractionBatch;
  extractionIndex: number;
  extractionIds: string[];
}

const ReviewGroup: React.FC<ReviewGroupProps> = ({
  ebId,
  extractionBatch,
  extractionIds,
  extractionIndex,
}) => {
  const { setIds } = React.useContext(AppContext);

  const [extractions, setExtractions] = React.useState<Array<IExtraction>>([]);
  const [loading, setLoading] = React.useState(true);
  const [docs, setDocs] = React.useState<IDocument[]>([]);
  const glynt = useGlynt();
  const client = useQueryClient();

  React.useEffect(() => {
    setIds(extractionIds);
    // eslint-disable-next-line
  }, []);

  // TODO: use IExtraction only
  React.useEffect(() => {
    let groupExtractions: Array<IExtraction> = [];
    // get document from extraction, one at a time
    let groupDocuments: IDocument[] = [];
    Promise.all(
      extractionIds
        .slice(extractionIndex, extractionIndex + 1)
        .map(async (extractionId: string, index: number) => {
          try {
            await client
              .fetchQuery({
                queryKey: ['extraction', extractionId],
                queryFn: async () => (await glynt).extraction.retrieve(extractionId),
              })

              .then((data) => {
                if (data) {
                  groupExtractions[index] = data;
                  let docId = getIdFromUrl(data.document);
                  client
                    .fetchQuery({
                      queryKey: ['document', docId],
                      queryFn: async () => (await glynt).document.retrieve(docId),
                    })
                    .then((data) => {
                      if (data) {
                        // groupExtractions[index].doc = result.data.doc;
                        groupDocuments[index] = data;
                      }
                    })
                    .catch(console.error);
                }
              });
          } catch (error) {
            console.error(error);
            // groupExtractions[index] = null;
          }
        })
    )
      .then(() => {
        setExtractions(groupExtractions);
        setDocs(groupDocuments);
        setLoading(false);
        // this will cause inifinte rerendering
        // setIds(extractionIds);
      })
      .catch(console.error);
    // eslint-disable-next-line
  }, [extractionIds, extractionIndex]);

  const currentExtraction = extractions[0];

  const [resultsType, setResultsType] = React.useState<IResultsType>('corrected');

  if (extractionIndex < 0) {
    return <DocNotFound />;
  }
  if (loading) {
    return <LinearProgress />;
  }
  return (
    <MarkContextProvider>
      <Grid container spacing={1} className="flex-no-shrink">
        <Grid item xs={12}>
          <ReviewNav
            steps={extractionIds.length}
            documents={docs}
            extractions={extractions}
            extractionIndex={extractionIndex}
            batchLabel={extractionBatch.label}
            resultsType={resultsType}
            setResultsType={setResultsType}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1} className="flex-section">
        <Grid item xs={3} className={'flex-col-scroll'}>
          <ReviewForm extractionId={currentExtraction.id} ebId={ebId} />
        </Grid>
        <Grid item xs={9} className={'flex-col-scroll'}>
          <ReviewDoc extraction={currentExtraction} />
        </Grid>
      </Grid>
    </MarkContextProvider>
  );
};

export default ReviewGroup;
